import { LocalizationService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormStateService } from '@digiwall/core';
import { ThirdPartyRole } from '@digiwall/goliath-core/proxy';
import {
  CustomerQuoteDto,
  CustomerSalesPurchaseDto,
  CustomerSalesPurchaseService,
} from '@digiwall/goliath-documents/proxy';
import moment from 'moment';
import { Observable, concatMap, of } from 'rxjs';

@Injectable()
export class CustomerQuoteToPurchaseOrderService {
  private confirmationService = inject(ConfirmationService);
  private localizationService = inject(LocalizationService);
  private salesPurchaseService = inject(CustomerSalesPurchaseService);
  private formStateService = inject(FormStateService);

  public toPurchaseOrder(
    quote: CustomerQuoteDto,
    form: FormGroup,
  ): Observable<CustomerSalesPurchaseDto> {
    if (!this.validateThirdPartyRole(quote)) return of(null);

    return this.formStateService.checkDirtyForm(form, () => {
      return this.convertToSalesPurchase(quote);
    });
  }

  private validateThirdPartyRole(quote: CustomerQuoteDto): boolean {
    if (quote.thirdPartyRole == ThirdPartyRole.Lead) {
      this.confirmationService.error(
        this.localizationService.instant('Doc::CustomerQuote:ToPurchaseOrder:LeadNotAllowed'),
        this.localizationService.instant('ERP::ErrorOccured'),
        {
          hideCancelBtn: true,
          yesText: 'AbpAccount::Close',
        },
      );
      return false;
    }
    return true;
  }

  private convertToSalesPurchase(quote: CustomerQuoteDto) {
    return this.salesPurchaseService.canCreateFromCustomerQuote(quote.id).pipe(
      concatMap(customerQuote => of(customerQuote != null)),
      concatMap(canConvert => {
        if (canConvert) {
          const warnMessage =
            moment(quote.validityDate) < moment(Date.now())
              ? 'Doc::CustomerQuote:ToPurchaseOrderWithExpiredValidityDate'
              : 'Doc::CustomerQuote:ToPurchaseOrder';

          return this.confirmationService.warn(warnMessage, '::AreYouSure');
        }
        return of(Confirmation.Status.reject);
      }),
      concatMap(confirmationResult => {
        const hasPositiveAmount = quote.totalAmount != null && quote.totalAmount >= 0;

        if (hasPositiveAmount || confirmationResult != Confirmation.Status.confirm)
          return of(confirmationResult);

        return this.confirmationService.warn(
          'Doc::NegativeTotalAmount:ContinueQuestion',
          '::AreYouSure',
        );
      }),
      concatMap(confirmationResult => {
        if (confirmationResult == Confirmation.Status.confirm) {
          return this.salesPurchaseService.createFromCustomerQuote(quote.id);
        }
        return of(null);
      }),
    );
  }
}
